import { displayFromCodeableConcept } from '@seen/core/codeableConcept'
import type { ScaleType } from '@seen/core/codings'
import { ExtendedObservation } from '@seen/core/models/observation'
import { orderBy, sortBy } from 'lodash-es'
import type { JSX, ReactNode } from 'react'
import { ActivityEntry } from '#src/components/activity/ActivityEntry.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { ObservationDisplay } from '#src/components/rich-text-editor/ObservationNode/ObservationDisplay.tsx'
import { useResourceVersions } from '#src/hooks/useResourceVersions.ts'

export function ObservationActivity({
  observationId,
  codingDisplay,
  scaleType,
}: {
  observationId: string
  codingDisplay: string
  scaleType: ScaleType
}): JSX.Element {
  const { data: pastObservations = [], isLoading } = useResourceVersions(
    ExtendedObservation,
    observationId
  )
  const activityData = observationsToActivityData(
    pastObservations,
    codingDisplay,
    scaleType
  )

  if (isLoading) {
    return <Loader />
  }

  return (
    <>
      {activityData.map((entry) => (
        <ActivityEntry
          key={entry.key ?? entry.observation.versionId}
          author={entry.observation.lastUpdatedBy}
          time={entry.observation.lastUpdated}
          action={entry.action}
        >
          {entry.children}
        </ActivityEntry>
      ))}
    </>
  )
}

function observationsToActivityData(
  observations: ExtendedObservation[],
  codingDisplay: string,
  scaleType: ScaleType
): {
  action: ReactNode
  observation: ExtendedObservation
  key?: string
  children?: ReactNode
}[] {
  const sortedObservations = sortBy(observations, (o) => o.lastUpdated)
  const activityDataDesc = sortedObservations.reduce<
    {
      action: ReactNode
      observation: ExtendedObservation
      key?: string
      children?: ReactNode
    }[]
  >((acc, curr) => {
    const currValue =
      curr.valueQuantity ??
      curr.valueString ??
      displayFromCodeableConcept(curr.valueCodeableConcept)
    if (acc.length === 0) {
      return [
        {
          action: 'created',
          observation: curr,
          children: (
            <div className="flex text-xs">
              <ObservationDisplay
                observation={curr}
                codingDisplay={codingDisplay}
                scaleType={scaleType}
                noHover
              />
            </div>
          ),
        },
      ]
    }
    const prev = acc[acc.length - 1]
    const prevValue =
      prev.observation.valueQuantity ??
      prev.observation.valueString ??
      displayFromCodeableConcept(prev.observation.valueCodeableConcept)
    if (
      currValue !== prevValue ||
      curr.noteFirstText !== prev.observation.noteFirstText
    ) {
      acc.push({
        action: 'updated',
        observation: curr,
        children: (
          <div className="flex gap-2 text-xs">
            <div className="flex flex-1 items-start rounded-sm bg-gray-50">
              <ObservationDisplay
                observation={prev.observation}
                codingDisplay={codingDisplay}
                scaleType={scaleType}
                noHover
              />
            </div>
            <div className="self-center text-foreground-secondary">→</div>
            <div className="flex flex-1 items-start rounded-sm bg-gray-50">
              <ObservationDisplay
                observation={curr}
                codingDisplay={codingDisplay}
                scaleType={scaleType}
                noHover
              />
            </div>
          </div>
        ),
      })
      return acc
    }
    return acc
  }, [])
  return orderBy(activityDataDesc, (a) => a.observation.lastUpdated, 'desc')
}
